import { CommonModule } from '@angular/common';
import { Component, effect, input, signal } from '@angular/core';

export interface ImageData {
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height: number;
  alt?: string;
}

@Component({
  selector: 'app-progressive-image',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative">
      @if (thumbnailLoaded() && imageData()?.thumbnailUrl) {
        <img
          [src]="imageData()?.thumbnailUrl"
          [alt]="imageData()?.alt || 'Image thumbnail'"
          [class]="imageClasses() + (fullImageLoaded() ? ' hidden' : '')"
          class="absolute inset-0"
          loading="eager"
          fetchpriority="high"
        />
      }

      @if (imageData()?.url) {
        <img
          #fullImage
          [src]="imageData()?.url"
          [alt]="imageData()?.alt || 'Image'"
          [width]="imageData()?.width"
          [height]="imageData()?.height"
          [class]="imageClasses() + (fullImageLoaded() ? '' : ' hidden')"
          class=""
          [loading]="loadingStrategy()"
          [attr.fetchpriority]="fetchPriority()"
          (load)="onFullImageLoad()"
          (error)="onImageError()"
        />
      }

      @if (imageError()) {
        <div [class]="imageClasses()" class="bg-gray-200 flex items-center justify-center">
          <div class="text-gray-500 text-center p-4">
            <svg class="w-8 h-8 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm">Bild konnte nicht geladen werden</span>
          </div>
        </div>
      }

      <ng-content></ng-content>
    </div>
  `,
})
export class ProgressiveImageComponent {
  imageData = input<ImageData | null>(null);
  imageClasses = input<string>('');
  loadingStrategy = input<'lazy' | 'eager'>('lazy');
  fetchPriority = input<'high' | 'low' | 'auto'>('auto');

  thumbnailLoaded = signal(false);
  fullImageLoaded = signal(false);
  imageError = signal(false);

  constructor() {
    effect(() => {
      const data = this.imageData();
      if (data?.thumbnailUrl) {
        this.preloadThumbnail(data.thumbnailUrl);
      } else if (data) {
        this.thumbnailLoaded.set(true);
      }
    });
  }

  onFullImageLoad(): void {
    this.fullImageLoaded.set(true);
  }

  onImageError(): void {
    this.imageError.set(true);
  }

  private preloadThumbnail(thumbnailUrl: string): void {
    const img = new Image();
    img.onload = () => this.thumbnailLoaded.set(true);
    img.onerror = () => {
      this.thumbnailLoaded.set(true);
    };
    img.src = thumbnailUrl;
  }
}
